// Supabase client configuration — safe to edit
// Note: types.ts is auto-generated by `supabase gen types` — do not edit types.ts directly
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Nest owns session refresh; supabase-js is the client-side token store only.
// The Supabase auto-refresh ticker is therefore DISABLED unconditionally: a
// Nest-minted refresh token cannot be rotated by GoTrue (which no longer exists
// in any environment), and a failed GoTrue refresh would wipe the stored
// session. useAuth runs the Nest POST /auth/refresh loop and re-injects rotated
// tokens via setSession().

// sessionStorage key for the user's active portal role (admin/owner/employee/tenant).
// Mirrors ACTIVE_ROLE_KEY in @/hooks/useAuth — duplicated here to avoid a circular
// import (useAuth → client → useAuth).
const ACTIVE_ROLE_STORAGE_KEY = 'dallali-active-role';

// Custom fetch wrapper that injects the user's current active role as an
// `x-active-role` request header on every Supabase call. PostgREST forwards
// request headers to the DB session, where RLS policies (defined in
// 20260505200000_active_role_aware_access_model.sql) read the header via
// public.get_active_role() to apply role-appropriate scope.
//
// Trust model: the header is user-controllable, but each policy CASE branch
// validates the user actually holds the claimed role server-side. A user
// passing `x-active-role: admin` without holding the admin role just falls
// through to a false predicate and sees no rows.
function withActiveRoleHeader(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const activeRole = typeof window !== 'undefined'
    ? sessionStorage.getItem(ACTIVE_ROLE_STORAGE_KEY)
    : null;
  const headers = new Headers(init?.headers);
  if (activeRole) {
    headers.set('x-active-role', activeRole);
  }
  return fetch(input, { ...init, headers });
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    // Disabled — see the refresh-ownership note above. useAuth owns refresh.
    autoRefreshToken: false,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
  global: {
    fetch: withActiveRoleHeader,
  },
});

// Anonymous client for public pages — never uses stored sessions
// This ensures public property pages work regardless of login state.
// No active-role header here: public pages rely on the
// "Public can view active properties / listings" policies, which don't
// require authentication or a role.
export const supabaseAnon = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    // Distinct storageKey so this second GoTrueClient doesn't collide with the
    // primary client's default key (both target the same project ⇒ same derived
    // key). The collision triggers supabase-js's "Multiple GoTrueClient instances
    // detected in the same browser context" warning on every page load and in
    // every test worker that imports this module. persistSession is false here,
    // so nothing is actually written under this key — it only de-dupes the
    // in-memory instance registry that powers the warning.
    storageKey: 'dallali-anon-auth',
  }
});